import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../hooks/useAuth';
import { ACTIONS } from '../../constants/actionTables';
import { requestSearch } from '../../helpers/search';
import { useMediaQuery } from '@mui/material';
import { iconButtonStyles } from '../../utils/iconButtonStyles';

import ModalStatusUser from '../ModalStatusUser';
import ModalUpdateUser from '../ModalUpdateUser';
import ModalStatusMachine from '../ModalStatusMachine';
import ModalAddLocation from '../ModalAddLocation';
import ModalAddMaintenanceType from '../ModalAddMaintenanceType';
import ModalAddMaintanenceTask from '../ModalAddMaintanenceTask';
import ModalAddSupplie from '../ModalAddSupplie';
import ModalDelete from '../ModalDelete';
import ModalAddGroup from '../ModalAddGroup';
import ModalStatusGroup from '../ModalStatusGroup';
import ModalManageCustomFields from '../ModalManageCustomFields';

import {
  IconButton,
  InputBase,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Box,
  Chip,
  Tooltip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ClearIcon from '@mui/icons-material/Clear';

function TableComponent({
  search = true,
  titles,
  data,
  action,
  fetchData,
  onSort,
  sortConfig,
  sortableColumns = [],
  columnKeys = null,
  // New props for integrated table header
  tableTitle,
  tableCount,
  showTableHeader = false,
  searchPlaceholder,
  onSearch, // Callback for search functionality
  onRowClick, // Callback for row click navigation
  initialSearchValue, // Initial value for search input
  onClearSearch, // Callback for clear search functionality
}) {
  const [rows, setRows] = useState([]);
  const [searchValue, setSearchValue] = useState(initialSearchValue || '');

  const { t } = useTranslation();
  const { user } = useAuth();
  const isMobile = useMediaQuery('(max-width:900px)');

  useEffect(() => {
    setRows(data);
  }, [data]);

  useEffect(() => {
    setSearchValue(initialSearchValue || '');
  }, [initialSearchValue]);

  const getColumnKey = (index) => {
    // Use custom column keys if provided, otherwise infer from data
    if (columnKeys) {
      return columnKeys[index];
    }

    // Default fallback: try to infer from first data row
    if (data && data.length > 0) {
      const keys = Object.keys(data[0]);
      return keys[index];
    }

    // Last resort: use legacy UsersList keys
    const legacyColumnKeys = ['number', 'firstname', 'lastname', 'email', 'role'];
    return legacyColumnKeys[index];
  };

  const isSortable = (index) => {
    const columnKey = getColumnKey(index);
    return sortableColumns.includes(columnKey);
  };

  const renderMobileCard = (row, rowIndex) => {
    return (
      <Paper
        key={rowIndex}
        onClick={() => onRowClick && onRowClick(row.id)}
        sx={{
          padding: '16px',
          marginBottom: '12px',
          borderRadius: '12px',
          border: '1px solid var(--table-border)',
          boxShadow: 'none',
          '&:hover': {
            backgroundColor: 'var(--table-hover)',
            transition: 'background-color 0.2s ease',
          },
          ...(onRowClick
            ? {
                cursor: 'pointer',
                userSelect: 'none',
              }
            : {}),
        }}
      >
        {row.image && (
          <div
            style={{
              marginBottom: '12px',
              height: '120px',
              overflow: 'hidden',
              borderRadius: '8px',
            }}
          >
            <img
              src={row.image}
              alt=""
              style={{
                height: '100%',
                borderRadius: '8px',
                objectFit: 'cover',
              }}
            />
          </div>
        )}

        {Object.keys(row).map((key, index) => {
          if (key === 'image' || key === 'id' || key === 'link' || key === 'flagForDelete') {
            return null;
          }

          // Get the title for this field
          const filteredKeys = Object.keys(row).filter(
            (k) => k !== 'image' && k !== 'id' && k !== 'link' && k !== 'flagForDelete'
          );
          const keyIndex = filteredKeys.indexOf(key);

          // Check if there's an image in the data to determine title offset
          const hasImage = row.image ? 1 : 0;
          const titleIndex = keyIndex + hasImage;
          const title = titles[titleIndex];

          const isLastItem = index === filteredKeys.length;

          return (
            <div
              key={index}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                marginBottom: '8px',
                fontSize: '0.875rem',
                borderBottom: isLastItem ? 'none' : '1px solid var(--table-border)',
                paddingBottom: isLastItem ? '0px' : '9px',
              }}
            >
              <span
                style={{
                  color: 'var(--text-secondary)',
                  fontWeight: 500,
                  minWidth: '180px',
                  width: '180px',
                  flexShrink: 0,
                }}
              >
                {title}:
              </span>
              <span
                style={{
                  color: 'var(--text-primary)',
                  textAlign: 'left',
                  flex: 1,
                  wordBreak: 'break-word',
                  display: 'flex',
                  justifyContent: 'flex-start',
                  alignItems: 'center',
                }}
              >
                {key === 'machinesCount' ? (
                  <Link
                    to={`/machines?group=${encodeURIComponent(row.name)}`}
                    style={{
                      color: '#3b82f6',
                      textDecoration: 'none',
                      fontWeight: 500,
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '4px',
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {row[key]} {row[key] === 1 ? t('generic.machine') : t('generic.machines')} →
                  </Link>
                ) : key === 'role' ? (
                  <Chip
                    label={row[key]}
                    variant="outlined"
                    color={row[key] === 'admin' ? 'primary' : 'default'}
                    size="medium"
                    sx={{
                      textTransform: 'capitalize',
                      fontWeight: 500,
                      minWidth: '80px',
                      width: '80px',
                      '& .MuiChip-label': {
                        paddingLeft: '12px',
                        paddingRight: '12px',
                        textAlign: 'center',
                      },
                      ...(row[key] === 'user'
                        ? {
                            color: '#6c757d',
                            borderColor: '#6c757d',
                          }
                        : {}),
                    }}
                  />
                ) : key === 'status' ? (
                  <Chip
                    label={row[key]}
                    variant="outlined"
                    size="medium"
                    sx={{
                      textTransform: 'capitalize',
                      fontWeight: 500,
                      minWidth: '80px',
                      width: '80px',
                      '& .MuiChip-label': {
                        paddingLeft: '12px',
                        paddingRight: '12px',
                        textAlign: 'center',
                      },
                      ...(row[key] === 'active'
                        ? {
                            color: '#22c55e',
                            borderColor: '#22c55e',
                            backgroundColor: '#f0fdf4',
                          }
                        : row[key] === 'inactive'
                        ? {
                            color: '#ef4444',
                            borderColor: '#ef4444',
                            backgroundColor: '#fef2f2',
                          }
                        : {}),
                    }}
                  />
                ) : key === 'machine_details' ? (
                  row[key] ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      <div
                        style={{
                          fontWeight: 600,
                          fontSize: '0.875rem',
                          color: 'var(--text-primary)',
                        }}
                      >
                        {row[key].name}
                      </div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                        {row[key].description}
                      </div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                        S/N: {row[key].serial_number}
                      </div>
                    </div>
                  ) : (
                    '-'
                  )
                ) : key === 'responsible_person' ? (
                  row[key] ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      <div style={{ fontSize: '0.875rem', color: 'var(--text-primary)' }}>
                        R.P: {row[key].responsibleName}
                      </div>
                      <div style={{ fontSize: '0.875rem', color: 'var(--text-primary)' }}>
                        D.P: {row[key].deputyName}
                      </div>
                    </div>
                  ) : (
                    '-'
                  )
                ) : key === 'machine_group' ? (
                  row[key] ? (
                    <Link
                      to={`/machines-groups/${row[key].id}`}
                      style={{
                        color: '#3b82f6',
                        textDecoration: 'none',
                        fontWeight: 500,
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {row[key].name}
                    </Link>
                  ) : (
                    '-'
                  )
                ) : (
                  row[key]
                )}
              </span>
            </div>
          );
        })}

        <div
          style={{
            marginTop: '12px',
            paddingTop: '12px',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '8px',
          }}
        >
          {renderAction(row)}
        </div>
      </Paper>
    );
  };

  const renderAction = (row) => {
    switch (action) {
      case ACTIONS.USERS_ACTIVE:
        return (
          <div
            style={{ display: 'flex', gap: '10px', justifyContent: 'end' }}
            onClick={(e) => e.stopPropagation()}
          >
            {row.id !== user.id && (
              <>
                <ModalUpdateUser id={row.id} />
                <ModalStatusUser id={row.id} action={'archived'} />
              </>
            )}
          </div>
        );
      case ACTIONS.USERS_ARCHIVED:
        return <ModalStatusUser id={row.id} action={'active'} />;
      case ACTIONS.ACTIVE_MACHINES:
        return (
          <div
            style={{ display: 'flex', gap: '10px', justifyContent: 'end' }}
            onClick={(e) => e.stopPropagation()}
          >
            {user?.role === 'admin' && <ModalStatusMachine id={row.id} action={'archived'} />}
          </div>
        );
      case ACTIONS.ARCHIVED_MACHINES:
        return (
          <div
            style={{ display: 'flex', gap: '10px', justifyContent: 'end' }}
            onClick={(e) => e.stopPropagation()}
          >
            {user?.role === 'admin' && <ModalStatusMachine id={row.id} action={'non-archived'} />}
          </div>
        );
      case ACTIONS.ACTIVE_MACHINES_GROUPS:
        return (
          <div
            style={{ display: 'flex', gap: '10px', justifyContent: 'end' }}
            onClick={(e) => e.stopPropagation()}
          >
            {user.role === 'admin' && <ModalAddGroup id={row.id} type={'update'} />}
          </div>
        );
      case ACTIONS.ARCHIVED_MACHINES_GROUPS:
        return <ModalStatusGroup id={row.id} action={'active'} />;
      case ACTIONS.SPECIFIC_GROUP_MAINTENANCE_TASK:
        return (
          <div
            style={{ display: 'flex', gap: '10px', justifyContent: 'end' }}
            onClick={(e) => e.stopPropagation()}
          >
            {user.role === 'admin' && (
              <ModalAddMaintanenceTask id={row.id} fetchData={fetchData} type={'edit'} />
            )}
            {user.role === 'admin' && (
              <ModalManageCustomFields maintenanceId={row.id} maintenanceName={row.name} />
            )}
            {user.role === 'admin' && (
              <ModalDelete
                id={row.id}
                name={row.name}
                type={ACTIONS.TASK}
                fetchData={fetchData}
                buttonStyle="outlined"
                buttonColor="error"
              />
            )}
          </div>
        );
      case ACTIONS.SPECIFIC_GROUP_SUPPLIES:
        return (
          <div
            style={{ display: 'flex', gap: '10px', justifyContent: 'end' }}
            onClick={(e) => e.stopPropagation()}
          >
            {!!row.link ? (
              <Link
                to={process.env.REACT_APP_BASE_URL + `supplies/file/${row.link}`}
                target="_blank"
              >
                <Tooltip title={t('generic.view')} placement="top">
                  <IconButton color="primary" style={iconButtonStyles}>
                    <VisibilityIcon />
                  </IconButton>
                </Tooltip>
              </Link>
            ) : (
              <Tooltip title={t('generic.noSafetySheet')} placement="top">
                <span>
                  <IconButton
                    disabled
                    style={{
                      ...iconButtonStyles,
                      color: 'rgba(0, 0, 0, 0.26)',
                      border: '1px solid rgba(0, 0, 0, 0.12)',
                    }}
                  >
                    <VisibilityIcon />
                  </IconButton>
                </span>
              </Tooltip>
            )}
            {user.role === 'admin' && (
              <ModalDelete
                id={row.id}
                name={row.name}
                type={ACTIONS.SUPPLY}
                fetchData={fetchData}
                buttonStyle="outlined"
                buttonColor="error"
              />
            )}
          </div>
        );
      case ACTIONS.SPECIFIC_GROUP_MANUALS:
        return (
          <div
            style={{ display: 'flex', gap: '10px', justifyContent: 'end' }}
            onClick={(e) => e.stopPropagation()}
          >
            {!!row.link ? (
              <Link
                to={process.env.REACT_APP_BASE_URL + `manuals/file/${row.link}`}
                target="_blank"
              >
                <Tooltip title={t('generic.view')} placement="top">
                  <IconButton color="primary" style={iconButtonStyles}>
                    <VisibilityIcon />
                  </IconButton>
                </Tooltip>
              </Link>
            ) : (
              <Tooltip title={t('generic.noManual')} placement="top">
                <span>
                  <IconButton
                    disabled
                    style={{
                      ...iconButtonStyles,
                      color: 'rgba(0, 0, 0, 0.26)',
                      border: '1px solid rgba(0, 0, 0, 0.12)',
                    }}
                  >
                    <VisibilityIcon />
                  </IconButton>
                </span>
              </Tooltip>
            )}
            {user.role === 'admin' && (
              <ModalDelete
                id={row.id}
                name={row.safetySheet}
                type={ACTIONS.MANUAL}
                fetchData={fetchData}
                buttonStyle="outlined"
                buttonColor="error"
              />
            )}
          </div>
        );
      case ACTIONS.SPECIFIC_MACHINE_DOCUMENTS:
        return (
          <div
            style={{ display: 'flex', gap: '10px', justifyContent: 'end' }}
            onClick={(e) => e.stopPropagation()}
          >
            <Link
              to={process.env.REACT_APP_BASE_URL + `validation-documents/file/${row.link}`}
              target="_blank"
            >
              <Tooltip title={t('generic.view')} placement="top">
                <IconButton color="primary" style={iconButtonStyles}>
                  <VisibilityIcon />
                </IconButton>
              </Tooltip>
            </Link>
            {user.role === 'admin' && (
              <ModalDelete
                id={row.id}
                name={row.document}
                type={ACTIONS.DOCUMENT}
                fetchData={fetchData}
                buttonStyle="outlined"
                buttonColor="error"
              />
            )}
          </div>
        );
      case ACTIONS.SPECIFIC_MACHINE_MANUALS:
        return !!row.link ? (
          <Link to={process.env.REACT_APP_BASE_URL + `manuals/file/${row.link}`} target="_blank">
            <Tooltip title={t('generic.view')} placement="top">
              <IconButton color="primary" style={iconButtonStyles}>
                <VisibilityIcon />
              </IconButton>
            </Tooltip>
          </Link>
        ) : (
          <Tooltip title={t('generic.noManual')} placement="top">
            <span>
              <IconButton disabled color="primary" style={iconButtonStyles}>
                <VisibilityIcon />
              </IconButton>
            </span>
          </Tooltip>
        );
      case ACTIONS.SPECIFIC_MACHINE_SUPPLY:
        return !!row.link ? (
          <Link to={process.env.REACT_APP_BASE_URL + `supplies/file/${row.link}`} target="_blank">
            <Tooltip title={t('generic.view')} placement="top">
              <IconButton color="primary" style={iconButtonStyles}>
                <VisibilityIcon />
              </IconButton>
            </Tooltip>
          </Link>
        ) : (
          <Tooltip title={t('generic.noSafetySheet')} placement="top">
            <span>
              <IconButton disabled color="primary" style={iconButtonStyles}>
                <VisibilityIcon />
              </IconButton>
            </span>
          </Tooltip>
        );
      case ACTIONS.SUPPLIES:
        return (
          <div
            style={{
              display: 'flex',
              gap: '10px',
              justifyContent: 'end',
              alignItems: 'center',
              minHeight: '40px',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {!!row.link ? (
              <Link
                to={process.env.REACT_APP_BASE_URL + `supplies/file/${row.link}`}
                target="_blank"
              >
                <Tooltip title={t('generic.view')} placement="top">
                  <IconButton color="primary" style={iconButtonStyles}>
                    <VisibilityIcon />
                  </IconButton>
                </Tooltip>
              </Link>
            ) : (
              <Tooltip title={t('generic.noSafetySheet')} placement="top">
                <span>
                  <IconButton disabled color="primary" style={iconButtonStyles}>
                    <VisibilityIcon />
                  </IconButton>
                </span>
              </Tooltip>
            )}

            {user.role === 'admin' && <ModalAddSupplie id={row.id} type={'update'} />}

            {user.role === 'admin' && (
              <ModalDelete
                id={row.id}
                name={row.name}
                type={ACTIONS.SUPPLIES}
                flagForDelete={row.flagForDelete}
                buttonStyle="outlined"
                buttonColor="error"
              />
            )}
          </div>
        );
      case ACTIONS.LOCATIONS:
        return (
          <div
            style={{ display: 'flex', gap: '10px', justifyContent: 'end' }}
            onClick={(e) => e.stopPropagation()}
          >
            {user.role === 'admin' && <ModalAddLocation id={row.id} type={'update'} />}
            {user.role === 'admin' && (
              <ModalDelete
                id={row.id}
                name={row.name}
                flagForDelete={row.flagForDelete}
                type={ACTIONS.LOCATIONS}
                buttonStyle="outlined"
                buttonColor="error"
              />
            )}
          </div>
        );
      case ACTIONS.MAINTENANCETYPES:
        return (
          <div
            style={{ display: 'flex', gap: '10px', justifyContent: 'end' }}
            onClick={(e) => e.stopPropagation()}
          >
            {user.role === 'admin' && (
              <ModalAddMaintenanceType id={row.id} type={'update'} />
            )}
            {user.role === 'admin' && (
              <ModalDelete
                id={row.id}
                name={row.name}
                flagForDelete={row.flagForDelete}
                type={ACTIONS.MAINTENANCETYPES}
                buttonStyle="outlined"
                buttonColor="error"
              />
            )}
          </div>
        );
      default:
        return null;
    }
  };

  // Render table header section
  const renderTableHeader = () => {
    if (!showTableHeader) return null;

    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', md: 'center' },
          padding: '16px',
          gap: { xs: '16px', md: 0 },
          backgroundColor: 'var(--table-row-white)',
          borderBottom: '1px solid var(--table-border)',
          borderRadius: { xs: '12px', md: '12px 12px 0 0' },
          marginBottom: { xs: '16px', md: 0 },
          border: '1px solid var(--table-border)',
        }}
      >
        <Box>
          <Typography variant="h6" fontWeight="600" color="var(--text-primary)">
            {tableTitle}
          </Typography>
          <Typography variant="body2" color="var(--text-secondary)">
            {tableCount}
          </Typography>
        </Box>

        {search && (
          <Paper
            component="form"
            sx={{
              display: 'flex',
              alignItems: 'center',
              width: { xs: '100%', md: 350 },
              maxWidth: { xs: 'none', md: 350 },
              border: '1px solid var(--border-default)',
              borderRadius: '12px',
              backgroundColor: 'var(--table-row-white)',
              boxShadow: 'none',
              '&:hover': {
                border: '1px solid var(--border-hover)',
              },
              '&:focus-within': {
                border: '1px solid var(--border-focus)',
                boxShadow: '0 0 0 3px rgb(59 130 246 / 0.1)',
              },
            }}
          >
            <IconButton
              type="button"
              sx={{
                p: '12px',
                color: 'var(--text-secondary)',
              }}
              aria-label="search"
            >
              <SearchIcon />
            </IconButton>
            <InputBase
              value={searchValue}
              sx={{
                ml: 1,
                flex: 1,
                fontSize: '0.875rem',
                '& input': {
                  padding: '12px 16px 12px 0',
                  color: 'var(--text-primary)',
                },
              }}
              placeholder={searchPlaceholder || t('generic.search')}
              onChange={(e) => {
                const newValue = e.target.value;
                setSearchValue(newValue);
                if (onSearch) {
                  onSearch(newValue);
                } else {
                  requestSearch(newValue, data, setRows);
                }
              }}
            />
            {searchValue && (
              <IconButton
                type="button"
                sx={{ p: '10px', color: 'var(--text-secondary)' }}
                onClick={() => {
                  setSearchValue('');
                  if (onClearSearch) {
                    onClearSearch();
                  } else if (onSearch) {
                    onSearch('');
                  } else {
                    requestSearch('', data, setRows);
                  }
                }}
              >
                <ClearIcon />
              </IconButton>
            )}
          </Paper>
        )}
      </Box>
    );
  };

  return (
    <div style={{ width: '100%' }}>
      {!showTableHeader && search && (
        <Paper
          component="form"
          sx={{
            display: 'flex',
            alignItems: 'center',
            width: isMobile ? '100%' : 350,
            mb: 3,
            border: '1px solid var(--border-default)',
            borderRadius: '12px',
            backgroundColor: 'var(--table-row-white)',
            boxShadow: 'none',
            '&:hover': {
              border: '1px solid var(--border-hover)',
            },
            '&:focus-within': {
              border: '1px solid var(--border-focus)',
              boxShadow: '0 0 0 3px rgb(59 130 246 / 0.1)',
            },
          }}
        >
          <IconButton
            type="button"
            sx={{
              p: '12px',
              color: 'var(--text-secondary)',
            }}
            aria-label="search"
          >
            <SearchIcon />
          </IconButton>
          <InputBase
            value={searchValue}
            sx={{
              ml: 1,
              flex: 1,
              fontSize: '0.875rem',
              '& input': {
                padding: '12px 16px 12px 0',
                color: 'var(--text-primary)',
              },
            }}
            placeholder={searchPlaceholder || t('generic.search')}
            onChange={(e) => {
              const newValue = e.target.value;
              setSearchValue(newValue);
              requestSearch(newValue, data, setRows);
            }}
          />
          {searchValue && (
            <IconButton
              type="button"
              sx={{ p: '10px', color: 'var(--text-secondary)' }}
              onClick={() => {
                setSearchValue('');
                if (onClearSearch) {
                  onClearSearch();
                } else if (onSearch) {
                  onSearch('');
                } else {
                  requestSearch('', data, setRows);
                }
              }}
            >
              <ClearIcon />
            </IconButton>
          )}
        </Paper>
      )}

      {/* Mobile View - Cards */}
      {isMobile ? (
        <>
          {showTableHeader && renderTableHeader()}
          <div style={{ width: '100%' }}>
            {rows && rows.length > 0 ? (
              rows.map((row, rowIndex) => renderMobileCard(row, rowIndex))
            ) : (
              <Paper
                sx={{
                  padding: '40px 20px',
                  textAlign: 'center',
                  color: 'var(--text-secondary)',
                  fontStyle: 'italic',
                  borderRadius: showTableHeader ? '0 0 12px 12px' : '12px',
                  border: '1px solid var(--table-border)',
                  borderTop: showTableHeader ? 'none' : '1px solid var(--table-border)',
                  boxShadow: 'none',
                }}
              >
                No data available
              </Paper>
            )}
          </div>
        </>
      ) : (
        /* Desktop View - Table */
        <>
          {showTableHeader && renderTableHeader()}
          <TableContainer
            component={Paper}
            sx={{
              marginBottom: '24px',
              boxShadow: 'none',
              borderRadius: showTableHeader ? '0 0 12px 12px' : '12px',
              border: '1px solid var(--table-border)',
              borderTop: showTableHeader ? 'none' : '1px solid var(--table-border)',
              overflowX: 'auto',
              overflowY: 'hidden',
              '&::-webkit-scrollbar': {
                height: '8px',
              },
              '&::-webkit-scrollbar-track': {
                backgroundColor: 'var(--scrollbar-track)',
                borderRadius: '4px',
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: 'var(--scrollbar-thumb)',
                borderRadius: '4px',
                '&:hover': {
                  backgroundColor: 'var(--scrollbar-thumb-hover)',
                },
              },
            }}
          >
            <Table
              sx={{
                width: '100%',
                '& .MuiTableCell-root': {
                  borderBottom: 'none !important',
                },
              }}
              aria-label="simple table"
            >
              <TableHead>
                <TableRow
                  sx={{
                    backgroundColor: 'var(--table-header-bg)',
                    '& .MuiTableCell-head': {
                      borderBottom: 'none',
                      fontWeight: 600,
                      fontSize: '0.875rem',
                      color: 'var(--table-header-text)',
                      padding: '16px 20px',
                    },
                    '& .MuiTableCell-head:first-of-type': {
                      borderRadius: showTableHeader ? '0' : '12px 0 0 0',
                    },
                    '& .MuiTableCell-head:last-of-type': {
                      borderRadius: showTableHeader ? '0' : '0 12px 0 0',
                    },
                  }}
                >
                  {titles.map((title, index) => {
                    const columnKey = getColumnKey(index);
                    const sortable = isSortable(index) && onSort;
                    const isActive = sortConfig && sortConfig.key === columnKey;

                    // Determine alignment based on column key
                    let alignment = 'left';
                    let justifyContent = 'flex-start';

                    if (columnKey === 'action') {
                      // Action column - right align
                      alignment = 'right';
                      justifyContent = 'flex-end';
                    } else if (columnKey === 'role' || columnKey === 'status') {
                      // User Group and Status columns - center align
                      alignment = 'center';
                      justifyContent = 'center';
                    }

                    return (
                      <TableCell
                        key={index}
                        align={alignment}
                        sx={{
                          ...(index === 0
                            ? {
                                width: titles.length === 3 ? '300px' : '100px',
                                minWidth: titles.length === 3 ? '300px' : '100px',
                              }
                            : {}),
                          ...(sortable
                            ? {
                                cursor: 'pointer',
                                userSelect: 'none',
                                '&:hover': {
                                  backgroundColor: 'var(--table-hover)',
                                },
                              }
                            : {}),
                        }}
                        onClick={sortable ? () => onSort(columnKey) : undefined}
                      >
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: justifyContent,
                            gap: '4px',
                          }}
                        >
                          {title}
                          {sortable && (
                            <span
                              style={{
                                display: 'inline-flex',
                                color: isActive ? '#3b82f6' : '#94a3b8',
                                fontSize: '18px',
                              }}
                            >
                              {!isActive ? (
                                <SwapVertIcon fontSize="small" />
                              ) : sortConfig.direction === 'asc' ? (
                                <ArrowUpwardIcon fontSize="small" />
                              ) : (
                                <ArrowDownwardIcon fontSize="small" />
                              )}
                            </span>
                          )}
                        </div>
                      </TableCell>
                    );
                  })}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows && rows.length > 0 ? (
                  rows.map((row, rowIndex) => (
                    <TableRow
                      key={rowIndex}
                      onClick={() => onRowClick && onRowClick(row.id)}
                      sx={{
                        borderBottom:
                          rowIndex === rows.length - 1 ? 'none' : '1px solid var(--table-border)',
                        backgroundColor:
                          rowIndex % 2 === 0 ? 'var(--table-row-white)' : 'var(--table-row-alt)',
                        '&:hover': {
                          backgroundColor: 'var(--table-hover)',
                          transition: 'background-color 0.2s ease',
                        },
                        '& .MuiTableCell-body': {
                          borderBottom: 'none',
                          color: 'var(--text-primary)',
                          fontSize: '0.875rem',
                          padding: '16px 20px',
                        },
                        ...(onRowClick
                          ? {
                              cursor: 'pointer',
                              userSelect: 'none',
                            }
                          : {}),
                      }}
                    >
                      {row.image && (
                        <TableCell key="image" align="left">
                          <img
                            src={row.image}
                            alt=""
                            style={{
                              maxWidth: '80px',
                              maxHeight: '80px',
                              borderRadius: '8px',
                              objectFit: 'cover',
                            }}
                          />
                        </TableCell>
                      )}
                      {Object.keys(row).map((key, cellIndex) => {
                        if (
                          key === 'image' ||
                          key === 'id' ||
                          key === 'link' ||
                          key === 'flagForDelete'
                        ) {
                          return null;
                        }

                        // Check if this is the first visible column (number column)
                        const visibleKeys = Object.keys(row).filter(
                          (k) =>
                            k !== 'image' && k !== 'id' && k !== 'link' && k !== 'flagForDelete'
                        );
                        const isFirstColumn = visibleKeys[0] === key;

                        return (
                          <TableCell
                            key={cellIndex}
                            align={key === 'role' || key === 'status' ? 'center' : 'left'}
                            sx={{
                              ...(isFirstColumn
                                ? {
                                    width: titles.length === 3 ? '300px' : '100px',
                                    minWidth: titles.length === 3 ? '300px' : '100px',
                                  }
                                : {}),
                              ...(key === 'email'
                                ? {
                                    wordBreak: 'break-all',
                                  }
                                : {}),
                            }}
                          >
                            {key === 'machinesCount' ? (
                              <Link
                                to={`/machines?group=${encodeURIComponent(row.name)}`}
                                style={{
                                  color: '#3b82f6',
                                  textDecoration: 'none',
                                  fontWeight: 500,
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '4px',
                                }}
                                onClick={(e) => e.stopPropagation()}
                              >
                                {row[key]}{' '}
                                {row[key] === 1 ? t('generic.machine') : t('generic.machines')} →
                              </Link>
                            ) : key === 'role' ? (
                              <Chip
                                label={row[key]}
                                variant="outlined"
                                color={row[key] === 'admin' ? 'primary' : 'default'}
                                size="small"
                                sx={{
                                  textTransform: 'capitalize',
                                  fontWeight: 500,
                                  minWidth: '80px',
                                  width: '80px',
                                  '& .MuiChip-label': {
                                    paddingLeft: '12px',
                                    paddingRight: '12px',
                                    textAlign: 'center',
                                  },
                                  ...(row[key] === 'user'
                                    ? {
                                        color: '#6c757d',
                                        borderColor: '#6c757d',
                                      }
                                    : {}),
                                }}
                              />
                            ) : key === 'status' ? (
                              <Chip
                                label={row[key]}
                                variant="outlined"
                                size="small"
                                sx={{
                                  textTransform: 'capitalize',
                                  fontWeight: 500,
                                  minWidth: '80px',
                                  width: '80px',
                                  '& .MuiChip-label': {
                                    paddingLeft: '12px',
                                    paddingRight: '12px',
                                    textAlign: 'center',
                                  },
                                  ...(row[key] === 'active'
                                    ? {
                                        color: '#22c55e',
                                        borderColor: '#22c55e',
                                        backgroundColor: '#f0fdf4',
                                      }
                                    : row[key] === 'inactive'
                                    ? {
                                        color: '#ef4444',
                                        borderColor: '#ef4444',
                                        backgroundColor: '#fef2f2',
                                      }
                                    : {}),
                                }}
                              />
                            ) : key === 'machine_details' ? (
                              row[key] ? (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                  <Typography
                                    component="div"
                                    sx={{
                                      fontWeight: 600,
                                      fontSize: '0.875rem',
                                      color: 'var(--text-primary)',
                                    }}
                                  >
                                    {row[key].name}
                                  </Typography>
                                  <Typography
                                    component="div"
                                    sx={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}
                                  >
                                    {row[key].description}
                                  </Typography>
                                  <Typography
                                    component="div"
                                    sx={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}
                                  >
                                    S/N: {row[key].serial_number}
                                  </Typography>
                                </div>
                              ) : (
                                '-'
                              )
                            ) : key === 'machine_group' ? (
                              row[key] ? (
                                <Link
                                  to={`/machines-groups/${row[key].id}`}
                                  style={{
                                    color: '#3b82f6',
                                    textDecoration: 'none',
                                    fontWeight: 500,
                                  }}
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  {row[key].name}
                                </Link>
                              ) : (
                                '-'
                              )
                            ) : key === 'responsible_person' ? (
                              row[key] ? (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                  <Typography
                                    component="div"
                                    sx={{ fontSize: '0.875rem', color: 'var(--text-primary)' }}
                                  >
                                    R.P: {row[key].responsibleName}
                                  </Typography>
                                  <Typography
                                    component="div"
                                    sx={{ fontSize: '0.875rem', color: 'var(--text-primary)' }}
                                  >
                                    D.P: {row[key].deputyName}
                                  </Typography>
                                </div>
                              ) : (
                                '-'
                              )
                            ) : (
                              row[key]
                            )}
                          </TableCell>
                        );
                      })}
                      {action && (
                        <TableCell align="right" sx={{ p: 0 }}>
                          {renderAction(row)}
                        </TableCell>
                      )}
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={titles.length}
                      align="center"
                      sx={{
                        color: 'var(--text-secondary)',
                        fontStyle: 'italic',
                        padding: '40px 20px',
                      }}
                    >
                      No data available
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </div>
  );
}

export default TableComponent;
